import { useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import { Select } from '../components/Select';
import { DatePicker, type DatePickerProps } from '../components/DatePicker';

/**
 * A labelled control.
 *
 * The label is always a real `<label>` wrapping its input rather than a
 * placeholder: a placeholder disappears the moment somebody types, which is
 * exactly when they look up to check what they are filling in.
 *
 * Inputs are 16px. Anything smaller makes iOS Safari zoom the page on focus
 * and leave it zoomed after the keyboard closes.
 */
const shell =
  'mt-1.5 w-full rounded-xl border border-shell-border bg-shell px-3.5 py-2.5 text-[16px] text-shell-text-active transition-colors placeholder:text-ink-400 hover:border-shell-text focus:border-brand-500';

export function Field({
  label,
  hint,
  className,
  ...props
}: { label: ReactNode; hint?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className={cn('block', className)}>
      <span className="text-sm font-medium text-shell-text">{label}</span>
      <input className={shell} {...props} />
      {hint !== undefined && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

export function TextField({
  label,
  hint,
  className,
  ...props
}: { label: ReactNode; hint?: ReactNode } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <label className={cn('block', className)}>
      <span className="text-sm font-medium text-shell-text">{label}</span>
      <textarea className={cn(shell, 'resize-y leading-relaxed')} {...props} />
      {hint !== undefined && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

/**
 * The same shell around a select, for forms that mix the two.
 *
 * Not a `<label>` around the control, as the two above are. `Select` is a
 * button, a button is labelable, and a label's own click is forwarded to the
 * control it labels — so every click opened the list and then immediately
 * closed it again. The label is tied to the control by id instead, which
 * reads the same to a screen reader and leaves one click as one click.
 */
export function SelectField({
  label,
  hint,
  className,
  children,
  id,
  ...props
}: {
  label: ReactNode;
  hint?: ReactNode;
} & React.SelectHTMLAttributes<HTMLSelectElement>) {
  const auto = useId();
  const control = id ?? auto;
  return (
    <div className={cn('block', className)}>
      <span id={`${control}-label`} className="text-sm font-medium text-shell-text">
        {label}
      </span>
      <Select id={control} aria-labelledby={`${control}-label`} className={cn(shell, 'appearance-none')} {...props}>
        {children}
      </Select>
      {hint !== undefined && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </div>
  );
}

/**
 * The same shell around the date picker.
 *
 * Tied to its control by id rather than wrapping it, for the reason written
 * above `SelectField`: the picker's trigger is a button, and a label's own
 * click is forwarded to the control it labels, so a wrapping label opened the
 * calendar and closed it again in the same click.
 */
export function DateField({
  label,
  hint,
  className,
  id,
  ...props
}: { label: ReactNode; hint?: ReactNode } & DatePickerProps) {
  const auto = useId();
  const control = id ?? auto;
  return (
    <div className={cn('block', className)}>
      <span id={`${control}-label`} className="text-sm font-medium text-shell-text">
        {label}
      </span>
      <DatePicker id={control} aria-labelledby={`${control}-label`} className={shell} {...props} />
      {hint !== undefined && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </div>
  );
}
